import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Wifi, BatteryCharging, MapPin, Users, Building2, Coffee, 
  ArrowRight, CheckCircle, Heart, Star, Plus, Clock, Shield, 
  BookOpen, Award, Globe, Lock, Mail, Phone
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Load LinkedIn badge script
    const script = document.createElement('script');
    script.src = 'https://platform.linkedin.com/badges/js/profile.js';
    script.async = true;
    script.defer = true;
    script.type = 'text/javascript';
    document.body.appendChild(script);
    
    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://platform.linkedin.com/badges/js/profile.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="About Strider | UK's Leading Third Workspace Platform"
        description="Strider helps professionals find verified third workspaces. Our mission: expand where the world can succeed through reliable, verified data about workspaces across the nation."
        keywords="about Strider, remote work UK, third workspace, Ian Barlow, verified workspaces"
        canonical="/about"
      />
      <Header />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-strider-teal via-teal-700 to-teal-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-teal-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 py-20 md:py-28 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Let's expand where the world can <span className="text-teal-200">succeed.</span>
            </h1>
            <p className="text-xl md:text-2xl text-teal-100 max-w-3xl mx-auto">
              Let's expand where the world can succeed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Problem - Meet Ian */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/ian-barlow-headshot.jpg" 
                  alt="Ian Barlow - Founder of Strider" 
                  className="w-full h-auto"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white font-bold text-xl">Ian Barlow</p>
                  <p className="text-teal-300">Founder & Creator of Strider</p>
                </div>
              </div>
              
              {/* LinkedIn Badge */}
              <div className="mt-6 flex justify-center">
                <div 
                  className="badge-base LI-profile-badge" 
                  data-locale="en_US" 
                  data-size="medium" 
                  data-theme="light" 
                  data-type="VERTICAL" 
                  data-vanity="ian-barlow-370295382" 
                  data-version="v1"
                >
                  <a 
                    className="badge-base__link LI-simple-link" 
                    href="https://uk.linkedin.com/in/ian-barlow-370295382?trk=profile-badge"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ian Barlow
                  </a>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-strider-teal rounded-full opacity-20 blur-2xl"></div>
            </motion.div>

            {/* Story */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="text-strider-teal font-semibold text-sm uppercase tracking-wider">The Story</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-strider-charcoal mt-2 mb-6">
                I was tired of not knowing where to go.
              </h2>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  Hi, I'm Ian. Like a lot of people, I don't always work in one place. Sometimes I'm between meetings, 
                  sometimes travelling, sometimes I just need a change of scenery.
                </p>
                <p>
                  And every time, it was the same problem: <em className="text-strider-charcoal font-medium">"Where can I actually 
                  sit down, open my laptop, and get things done without feeling awkward?"</em>
                </p>
                <p>
                  Sure, there are plenty of cafes, pubs, and spaces out there... but not all of them are set up for it. 
                  Some don't have plug sockets. Some have unreliable WiFi. Some just aren't the right environment.
                </p>
                <p className="text-strider-teal font-semibold text-xl">
                  So I built Strider.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Strider */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-strider-teal font-semibold text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-strider-charcoal mt-2 mb-4">
              Find places you can actually <span className="text-strider-teal">work</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Not just sit. Real venues, reviewed by real people, based on the things that actually matter.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Wifi, label: "Verified WiFi", desc: "Measured speeds" },
              { icon: BatteryCharging, label: "Power Outlets", desc: "Stay charged up" },
              { icon: Coffee, label: "Food & Drinks", desc: "Fuel your work" },
              { icon: Star, label: "Atmosphere", desc: "Find your vibe" }
            ].map((item, idx) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow text-center"
              >
                <div className="w-14 h-14 bg-strider-teal/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-strider-teal" />
                </div>
                <h3 className="font-bold text-strider-charcoal mb-1">{item.label}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="py-16 md:py-24 bg-strider-teal text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              This isn't a corporate directory.
            </h2>
            <p className="text-xl text-teal-100 max-w-2xl mx-auto">
              It's not a paid listing site pretending to be helpful. It's built from real experiences by real people.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Community Driven", desc: "Add places you love, warn others about places that don't work." },
              { title: "Completely Free", desc: "For users, it's always free. Search, explore, and contribute." },
              { title: "Genuinely Useful", desc: "Turn 'dead time' into productive or enjoyable time." }
            ].map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
              >
                <CheckCircle className="w-8 h-8 text-teal-200 mb-4" />
                <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                <p className="text-teal-100">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits for Users */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16">
            {/* For Users */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">For Remote Workers</h3>
              </div>
              <ul className="space-y-4">
                {[
                  "Discover new places you wouldn't normally try",
                  "Avoid wasting time walking into the wrong venue",
                  "Turn dead time into productive time",
                  "Work somewhere that actually suits your needs"
                ].map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                    <span className="text-gray-600">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/browse" 
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-brand text-white rounded-lg font-semibold hover:bg-brand-dark transition-colors"
              >
                Find a Workspace <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* For Venues */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">For Venues</h3>
              </div>
              <ul className="space-y-4">
                {[
                  "Increased daytime footfall",
                  "New customers who stay longer",
                  "More consistent off-peak revenue",
                  "Exposure to a growing, targeted audience",
                  "Reach people who may never have found you"
                ].map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                    <span className="text-gray-600">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/for-venues" 
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors"
              >
                List Your Venue <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Bigger Picture */}
      <section className="py-16 md:py-24 bg-strider-charcoal text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-12 h-12 text-red-400 mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              The Bigger Picture
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              At its heart, Strider is about making better use of the spaces around us. 
              It's about helping people break out of routine, discover somewhere new, and make their 
              day a little better — while helping great venues get the attention they deserve.
            </p>
            <p className="text-lg text-gray-400 italic">
              If you've ever wandered around with a laptop wondering where to go next… this is for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-strider-teal to-teal-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Know a great spot?
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              Add it. Share it. Help someone else find it.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/add-location" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-strider-teal rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              >
                <Plus className="w-5 h-5" /> Add a Location
              </Link>
              <Link 
                to="/" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white border-2 border-white/30 rounded-lg font-bold text-lg hover:bg-white/20 transition-colors"
              >
                <MapPin className="w-5 h-5" /> Explore the Map
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer tagline */}
      <div className="bg-strider-charcoal py-12 text-center">
        <p className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-4">
          Let's expand where the world can succeed.
        </p>
        <p className="text-gray-400 text-lg">
          Strider — Verified workspaces for professionals everywhere
        </p>
      </div>

      {/* Company Information Section - E-E-A-T Compliance */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-8">
              <Shield className="w-8 h-8 text-brand" />
              <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-brand" />
                  Registered Company
                </h3>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Strider</strong></p>
                  <p>A trading name of Boldhorn Press LTD</p>
                  <p>Company Registration: England & Wales</p>
                  <p className="text-sm text-gray-500 mt-4">
                    Building technology infrastructure that serves professionals 
                    while maintaining data protection principles.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-brand" />
                  Contact & Support
                </h3>
                <div className="space-y-3 text-gray-600">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a href="mailto:hello@strider.work" className="text-brand hover:underline">hello@strider.work</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <a href="mailto:privacy@strider.work" className="text-brand hover:underline">privacy@strider.work</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <span>www.strider.work</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-4">
                    Response time: Within 2 business days
                  </p>
                </div>
              </div>
            </div>

            {/* Our Mission */}
            <div className="mt-8 bg-gradient-to-br from-brand/5 to-brand/10 rounded-xl p-8 border border-brand/20">
              <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-6 h-6 text-brand" />
                Our Mission
              </h3>
              <div className="space-y-4 text-gray-700">
                <p>
                  We believe that data about workplaces should be collected, processed, and stored 
                  according to the highest standards. Strider represents our commitment to building 
                  productivity infrastructure that serves the modern workforce.
                </p>
                <p>
                  We employ AI-driven data ingestion to verify and maintain workspace information, but always 
                  with human oversight and GDPR-compliant practices. Our goal: help professionals 
                  bridge the productivity gap by providing reliable, verified data about third workspaces 
                  across the nation.
                </p>
                <p className="font-medium text-brand">
                  Every venue listing. Every WiFi speed test. Every review. Expanding where you can succeed.
                </p>
              </div>
            </div>

            {/* Data Practices */}
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <Lock className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">GDPR Compliant</h4>
                <p className="text-sm text-gray-600">
                  Full compliance with UK GDPR and Data Protection Act 2018
                </p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <Shield className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">UK Data Hosting</h4>
                <p className="text-sm text-gray-600">
                  User data processed and stored within United Kingdom jurisdiction
                </p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <BookOpen className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">Transparent Practices</h4>
                <p className="text-sm text-gray-600">
                  Clear documentation of our data collection and AI usage
                </p>
              </div>
            </div>

            {/* Legal Links */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-4">Legal & Compliance</h4>
              <div className="flex flex-wrap gap-4">
                <Link to="/privacy" className="text-brand hover:underline flex items-center gap-1">
                  <Lock className="w-4 h-4" /> Privacy Policy
                </Link>
                <Link to="/terms" className="text-brand hover:underline flex items-center gap-1">
                  <BookOpen className="w-4 h-4" /> Terms of Service
                </Link>
                <Link to="/cookies" className="text-brand hover:underline flex items-center gap-1">
                  <Shield className="w-4 h-4" /> Cookie Policy
                </Link>
                <Link to="/contributor-terms" className="text-brand hover:underline flex items-center gap-1">
                  <Users className="w-4 h-4" /> Contributor Guidelines
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
